import React, { useCallback, useEffect, useState } from 'react';
import { Card } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { Spinner } from '../../../components/ui/Spinner';
import {
  formatKobo,
  formatMicroUsd,
  plansApi,
  type PlanEconomics,
  type PurposeCostRow,
} from '../../../api/plans';

/**
 * What each tier earns against what it costs to serve.
 *
 * The half of a pricing decision the catalogue could not show. Prices were set
 * against the market — what Moniepoint gives away, what Bumpa charges — and
 * never against what a customer on that tier actually consumes.
 *
 * Two revenue figures on purpose. **MRR** is list price × subscribers: the
 * projection a price change is argued from. **Collected** is money that
 * actually arrived. They diverge for real reasons — an annual term billed in
 * one month, a grandfathered rate, a failed debit — and showing only the
 * first would describe revenue nobody received.
 */

const WINDOWS = [7, 30, 90];

function Metric({
  label,
  value,
  hint,
  tone,
}: {
  label: string;
  value: string;
  hint?: string;
  tone?: 'good' | 'bad';
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <div
        style={{
          fontSize: 11,
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          color: 'var(--neutral-600)',
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 18,
          fontWeight: 600,
          fontVariantNumeric: 'tabular-nums',
          color:
            tone === 'bad'
              ? 'var(--danger-900)'
              : tone === 'good'
                ? 'var(--green-900)'
                : 'var(--neutral-800)',
        }}
      >
        {value}
      </div>
      {hint && (
        <div style={{ fontSize: 11, color: 'var(--neutral-600)' }}>{hint}</div>
      )}
    </div>
  );
}

export const Economics: React.FC = () => {
  const [days, setDays] = useState(30);
  const [data, setData] = useState<PlanEconomics | null>(null);
  const [purposes, setPurposes] = useState<PurposeCostRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [economics, byPurpose] = await Promise.all([
        plansApi.economics(days),
        plansApi.purposeCosts(days),
      ]);
      setData(economics);
      setPurposes(byPurpose);
    } catch {
      setError('Could not load plan economics.');
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
        <Spinner />
      </div>
    );
  }
  if (error || !data) {
    return <div style={{ color: 'var(--danger-900)', fontSize: 13 }}>{error}</div>;
  }

  const nothingRecorded =
    purposes.length === 0 && data.plans.every((p) => p.modelMicroUsd === 0);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {WINDOWS.map((w) => (
          <Button
            key={w}
            size="sm"
            variant={w === days ? 'primary' : 'secondary'}
            onClick={() => setDays(w)}
          >
            {w} days
          </Button>
        ))}
        <div style={{ flex: 1 }} />
        {data.fxConfigured ? (
          <span style={{ fontSize: 12, color: 'var(--neutral-600)' }}>
            Converted at ₦{data.ngnPerUsd.toLocaleString()} / $
          </span>
        ) : (
          <span style={{ fontSize: 12, color: 'var(--warning-900)' }}>
            No FX rate configured — every naira cost below reads ₦0
          </span>
        )}
      </div>

      {nothingRecorded && (
        <Card>
          <div style={{ fontSize: 13, color: 'var(--neutral-700)', lineHeight: 1.6 }}>
            <strong>No model usage recorded in this window yet.</strong> Costs
            are metered from the moment a conversation happens, so this fills in
            as the app is used. Until it does, the margins below are the list
            prices with nothing subtracted — not a finding.
          </div>
        </Card>
      )}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: 14,
        }}
      >
        {data.plans.map((plan) => (
          <Card key={plan.planKey}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'baseline',
                  justifyContent: 'space-between',
                }}
              >
                <div style={{ fontSize: 16, fontWeight: 600 }}>{plan.planName}</div>
                <div style={{ fontSize: 12, color: 'var(--neutral-600)' }}>
                  {plan.subscribers}{' '}
                  {plan.subscribers === 1 ? 'business' : 'businesses'}
                </div>
              </div>

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: 12,
                }}
              >
                <Metric
                  label="MRR"
                  value={formatKobo(plan.mrrKobo)}
                  hint={`${formatKobo(plan.monthlyPriceKobo)} × ${plan.subscribers}`}
                />
                <Metric
                  label="Collected"
                  value={formatKobo(plan.collectedKobo)}
                  hint="actually arrived"
                />
                <Metric
                  label="Model cost"
                  value={formatKobo(plan.modelCostKobo)}
                  hint={formatMicroUsd(plan.modelMicroUsd)}
                />
                <Metric
                  label="Margin"
                  value={formatKobo(plan.marginKobo)}
                  hint="MRR less model cost"
                  tone={plan.marginKobo < 0 ? 'bad' : undefined}
                />
              </div>

              {plan.subscribers > 0 && (
                <div
                  style={{
                    fontSize: 12,
                    color: 'var(--neutral-700)',
                    paddingTop: 10,
                    borderTop: '1px solid var(--neutral-200)',
                  }}
                >
                  {formatKobo(plan.modelCostPerBusinessKobo)} per business —{' '}
                  {plan.monthlyPriceKobo > 0
                    ? `${Math.round(
                        (plan.modelCostPerBusinessKobo / plan.monthlyPriceKobo) * 100,
                      )}% of the price`
                    : 'on a free plan'}
                </div>
              )}
            </div>
          </Card>
        ))}
      </div>

      <Card>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ fontSize: 14, fontWeight: 600 }}>Where the model spend goes</div>
          <div style={{ fontSize: 12, color: 'var(--neutral-600)' }}>
            Cost per call is the figure an allowance is set against — a cap of
            50 is only as safe as what one of them costs.
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table
              style={{ borderCollapse: 'collapse', width: '100%', minWidth: 620 }}
            >
              <thead>
                <tr>
                  {['Purpose', 'Model', 'Calls', 'Tokens in / out', 'Per call', 'Total'].map(
                    (h) => (
                      <th
                        key={h}
                        style={{
                          textAlign: 'left',
                          padding: '8px 12px',
                          fontSize: 11,
                          textTransform: 'uppercase',
                          letterSpacing: '0.08em',
                          color: 'var(--neutral-600)',
                          borderBottom: '1px solid var(--neutral-300)',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {h}
                      </th>
                    ),
                  )}
                </tr>
              </thead>
              <tbody>
                {purposes.length === 0 ? (
                  <tr>
                    <td
                      colSpan={6}
                      style={{
                        padding: '14px 12px',
                        fontSize: 13,
                        color: 'var(--neutral-600)',
                      }}
                    >
                      Nothing recorded in this window.
                    </td>
                  </tr>
                ) : (
                  purposes.map((row) => (
                    <tr key={`${row.purpose}:${row.model}`}>
                      <td style={cell}>{row.purpose}</td>
                      <td style={{ ...cell, color: 'var(--neutral-600)' }}>{row.model}</td>
                      <td style={num}>{row.calls.toLocaleString()}</td>
                      <td style={num}>
                        {row.inputTokens.toLocaleString()} /{' '}
                        {row.outputTokens.toLocaleString()}
                      </td>
                      <td style={{ ...num, fontWeight: 600 }}>
                        {formatMicroUsd(row.microUsdPerCall)}
                      </td>
                      <td style={num}>{formatMicroUsd(row.microUsd)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {data.overhead.modelMicroUsd > 0 && (
            <div
              style={{
                fontSize: 12,
                color: 'var(--neutral-700)',
                paddingTop: 10,
                borderTop: '1px solid var(--neutral-200)',
              }}
            >
              <strong>Platform overhead: {formatMicroUsd(data.overhead.modelMicroUsd)}</strong>{' '}
              ({formatKobo(data.overhead.modelCostKobo)}). {data.overhead.note}
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

const cell: React.CSSProperties = {
  padding: '10px 12px',
  fontSize: 13,
  borderBottom: '1px solid var(--neutral-200)',
};

const num: React.CSSProperties = {
  ...cell,
  fontVariantNumeric: 'tabular-nums',
  whiteSpace: 'nowrap',
};
